/**
 * Pure matching logic for the `SequenceWatcher` (chantier 8b).
 *
 * A sequence is an ordered list of *groups*. A group is a set of MIDI
 * notes that the player must hold together (a single melody note is
 * just a 1-element group; a chord is an N-element group).
 *
 * The matcher is intentionally state-thin: it owns which group it waits
 * for, and nothing else. The React layer drives press events, tracks
 * which keys are down, and decides what to do on group confirmation /
 * wrong note.
 *
 * Why a pure helper rather than inlining in the component
 * -------------------------------------------------------
 *
 * The component itself runs in `'use client'` and mounts inside React,
 * which makes Jest unit testing painful in this repo (`testEnvironment:
 * 'node'`). Extracting the FSM-like state into a plain class keeps the
 * acceptance logic exercisable in deterministic Jest tests, and keeps
 * the React layer to a thin adapter.
 *
 * Behaviour on a wrong note (design choice)
 * -----------------------------------------
 *
 * When the player presses a midi that is **not** part of the current
 * group, we report it as an "error" but do **NOT** reset the matcher.
 * The half-taken group is kept; the player can still complete it.
 * Rationale: the no-rhythm practice phase (S1b/S2b/S3) is forgiving by
 * spec (§"Pratique sans rythme"). The user can wander on the keyboard
 * and the matcher stays patient.
 *
 * Acceptance rule: the keys that are **down**, not the keys that were
 * *struck in time*
 * -----------------------------------------------------------------
 *
 * A group is accepted as soon as the set of currently held keys contains
 * every midi of the group. There is **no time window**, which is the same
 * rule S1a validates a grip with, and it is deliberate.
 *
 * This replaced a 100 ms acceptance window that measured the delay
 * between the first and last key of a chord. That window rejected, in
 * total silence, every chord taken the way a beginner takes one — one
 * finger after another. Worse, each late press *restarted* the window,
 * so the three presses of a triad each reported `progress` and the group
 * never confirmed: no error was raised, so the UI's retry affordance —
 * gated on the error count — never appeared either. It also deadlocked a
 * group holding a note sustained from the previous one, since that note
 * would never be pressed a second time. 35.7% of left-hand groups and
 * 27.2% of right-hand groups carry two keys or more (PDMX 450, 95 432
 * and 108 781 groups), so this was not an edge case.
 *
 * Simultaneity is still imposed, but by physics rather than by a clock:
 * the keys have to be **down at the same instant**. Pressing and
 * releasing them one by one never confirms anything. What the rule gives
 * up is that a key held over from the previous group counts as taken
 * without being struck again — the forgiving direction, and the one that
 * cannot lock anybody out.
 *
 * Containment and not equality: a note still sustained from an earlier
 * group must not block the next one.
 */

// ── Types ────────────────────────────────────────────────────────

/**
 * One step of the expected sequence. `midis` lists the notes that must
 * be held together. Order does not matter inside a group.
 */
export type SequenceGroup = {
  readonly midis: readonly number[];
};

export type PressOutcome =
  | { readonly kind: 'progress' }
  | { readonly kind: 'wrongNote'; readonly midi: number }
  | { readonly kind: 'groupConfirmed'; readonly groupIndex: number; readonly nextIndex: number }
  | { readonly kind: 'sequenceCompleted'; readonly groupIndex: number }
  | { readonly kind: 'completed' };

// ── Implementation ───────────────────────────────────────────────

/**
 * Stateful matcher. Construct once per sequence, feed press events,
 * inspect the returned outcome.
 *
 * Not React-aware: the React layer (`SequenceWatcher`) is responsible
 * for instantiating it on mount, recreating it when `groups` change,
 * tracking held keys, and translating outcomes into callbacks.
 */
export class SequenceMatcher {
  private readonly groups: readonly SequenceGroup[];

  private currentIndex = 0;

  constructor(groups: readonly SequenceGroup[]) {
    this.groups = groups;
  }

  /** Index of the group the matcher is currently waiting for. */
  getCurrentIndex(): number {
    return this.currentIndex;
  }

  /** True when every group has been confirmed. */
  isCompleted(): boolean {
    return this.currentIndex >= this.groups.length;
  }

  /**
   * Feed a press event. `held` is the set of keys down **after** this
   * press, the pressed midi included. Returns the outcome the React
   * layer should react to.
   *
   * - `wrongNote`, the midi is not part of the current group. Progress
   *   is preserved (cf. design note above).
   * - `progress`, a midi of the current group was pressed but the group
   *   is not fully held yet.
   * - `groupConfirmed`, the current group is held; `nextIndex` points at
   *   the next group (or `groups.length` if none).
   * - `sequenceCompleted`, variant of `groupConfirmed` for the very last
   *   group of the sequence.
   * - `completed`, a press received after the sequence has already
   *   completed. The React layer typically ignores it.
   */
  press(midi: number, held: ReadonlySet<number>): PressOutcome {
    if (this.isCompleted()) {
      return { kind: 'completed' };
    }

    const group = this.groups[this.currentIndex];

    if (!group.midis.includes(midi)) {
      return { kind: 'wrongNote', midi };
    }

    if (!group.midis.every((m) => held.has(m))) {
      return { kind: 'progress' };
    }

    const groupIndex = this.currentIndex;
    this.currentIndex += 1;

    if (this.currentIndex >= this.groups.length) {
      return { kind: 'sequenceCompleted', groupIndex };
    }

    return {
      kind: 'groupConfirmed',
      groupIndex,
      nextIndex: this.currentIndex,
    };
  }

  /**
   * Resets the matcher to the very first group. Mostly useful for the
   * UI "retry" affordance and tests; the React component does not call
   * it on wrong notes by design (see top-level note).
   */
  reset(): void {
    this.currentIndex = 0;
  }

  /**
   * Points the matcher at `index`, so the sequence can be taken up part-way
   * through rather than from its first group.
   *
   * What the player asks for by scrolling the roll to the bar they want to
   * work: the run resumes at the attack under the playhead. Clamped, since
   * the index comes from a position on screen and not from the sequence.
   */
  seekTo(index: number): void {
    this.currentIndex = Math.min(
      Math.max(0, Math.trunc(index)),
      this.groups.length,
    );
  }
}

// ── Helpers (used by both the watcher and the state views) ───────

/**
 * Group consecutive `PhraseNote`s that share the same `startTick` into
 * sequence groups, ordered by start tick. A pure transform suitable for
 * use anywhere a `Phrase` slice is to be turned into a sequence.
 *
 * The input is expected to be the slice for one hand or the union of
 * both hands; sorting is performed defensively so the caller can pass
 * either.
 */
export function groupNotesByStartTick(
  notes: readonly { readonly midi: number; readonly startTick: number }[],
): SequenceGroup[] {
  if (notes.length === 0) {
    return [];
  }

  const sorted = [...notes].sort((a, b) => a.startTick - b.startTick);
  const groups: { startTick: number; midis: number[] }[] = [];
  let current: { startTick: number; midis: number[] } | null = null;

  for (const note of sorted) {
    if (current === null || note.startTick !== current.startTick) {
      current = { startTick: note.startTick, midis: [note.midi] };
      groups.push(current);
      continue;
    }

    if (!current.midis.includes(note.midi)) {
      current.midis.push(note.midi);
    }
  }

  return groups.map((g) => ({ midis: g.midis }));
}
